import { useState, useEffect } from 'react';
import {
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Minus,
  ShieldCheck,
  Clock,
  CalendarClock,
  Loader2,
  CheckCircle2,
  MinusCircle,
  PauseCircle,
  CalendarCheck2,
  LayoutGrid,
  Plus,
  Pencil,
  X,
  type LucideIcon,
} from 'lucide-react';
import { api, getErrorMessage } from '@/shared/services/api';
import toast from 'react-hot-toast';
import { CreateTaskModal } from '@/features/tasks/components/CreateTaskModal';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { cn } from '@/shared/utils/cn';

interface MatrixItem {
  id: number;
  client: {
    id: number;
    name: string;
  };
  feedItem: {
    id: number;
    title: string;
    description: string;
    publishedDate: string;
    link: string;
    rssFeedName: string;
    productType: string;
  };
  status: string;
  vulnerabilityLevel?: string;
  plannedUpdateDate?: string;
  completedUpdateDate?: string;
  notes?: string;
  createdAt: string;
  updatedAt?: string;
}

/** The fields SC-256 makes editable on an existing assignment. */
interface MatrixEditDraft {
  status: string;
  vulnerabilityLevel: string;
  plannedUpdateDate: string;
  completedUpdateDate: string;
  notes: string;
}

const EMPTY_MATRIX_DRAFT: MatrixEditDraft = {
  status: 'Pending',
  vulnerabilityLevel: '',
  plannedUpdateDate: '',
  completedUpdateDate: '',
  notes: '',
};

/** An ISO timestamp reduced to the `yyyy-MM-dd` a date input expects. */
const toDateInput = (value?: string): string => (value ? value.slice(0, 10) : '');

interface StatusMeta {
  label: string;
  badge: string;
  dot: string;
  icon: LucideIcon;
}

// Status owns a semantic hue token + a dot + an icon so the state never reads by color alone.
const STATUS_META: Record<string, StatusMeta> = {
  Pending: { label: 'Pending', badge: 'badge-neutral', dot: 'bg-muted-foreground', icon: Clock },
  Planned: { label: 'Planned', badge: 'badge-info', dot: 'bg-info', icon: CalendarClock },
  InProgress: { label: 'In Progress', badge: 'badge-warning', dot: 'bg-warning', icon: Loader2 },
  Completed: { label: 'Completed', badge: 'badge-success', dot: 'bg-success', icon: CheckCircle2 },
  NotApplicable: { label: 'Not Applicable', badge: 'badge-neutral', dot: 'bg-muted-foreground', icon: MinusCircle },
  OnHold: { label: 'On Hold', badge: 'badge-warning', dot: 'bg-warning', icon: PauseCircle },
};

interface VulnerabilityMeta {
  label: string;
  /** severity token class from index.css (.sev-*) */
  sev: string;
  icon: LucideIcon;
}

// Vulnerability rides the red→emerald severity heat scale; icon + label carry the non-color cue.
const VULNERABILITY_META: Record<string, VulnerabilityMeta> = {
  None: { label: 'None', sev: 'sev-none', icon: ShieldCheck },
  Low: { label: 'Low', sev: 'sev-low', icon: ArrowDown },
  Medium: { label: 'Medium', sev: 'sev-medium', icon: Minus },
  High: { label: 'High', sev: 'sev-high', icon: ArrowUp },
  Critical: { label: 'Critical', sev: 'sev-critical', icon: AlertTriangle },
};

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? {
    label: status,
    badge: 'badge-neutral',
    dot: 'bg-muted-foreground',
    icon: Clock,
  };
  return (
    <span className={meta.badge} title={`Status: ${meta.label}`} aria-label={`Status: ${meta.label}`}>
      <span className={cn('h-2 w-2 rounded-full', meta.dot)} aria-hidden="true" />
      {meta.label}
    </span>
  );
}

function VulnerabilityBadge({ level }: { level: string }) {
  const meta = VULNERABILITY_META[level];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      className={cn('sev', meta.sev)}
      title={`Vulnerability: ${meta.label}`}
      aria-label={`Vulnerability: ${meta.label}`}
    >
      <Icon className="h-3 w-3" aria-hidden="true" />
      {meta.label}
    </span>
  );
}

export function ClientMatrix() {
  const [matrixData, setMatrixData] = useState<MatrixItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterClient, setFilterClient] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterVulnerability, setFilterVulnerability] = useState<string>('');
  const [isCreateTaskModalOpen, setIsCreateTaskModalOpen] = useState(false);
  const [selectedMatrixItem, setSelectedMatrixItem] = useState<MatrixItem | null>(null);
  // SC-256: editing an assignment in place, rather than re-running the Assign popup from the
  // feed item (which breaks the flow and used to wipe the notes).
  const [editingItem, setEditingItem] = useState<MatrixItem | null>(null);
  const [editDraft, setEditDraft] = useState<MatrixEditDraft>(EMPTY_MATRIX_DRAFT);
  const [savingEdit, setSavingEdit] = useState(false);

  useEffect(() => {
    loadMatrixData();
  }, []);

  const startEditAssignment = (item: MatrixItem) => {
    setEditingItem(item);
    setEditDraft({
      status: item.status,
      vulnerabilityLevel: item.vulnerabilityLevel ?? '',
      plannedUpdateDate: toDateInput(item.plannedUpdateDate),
      completedUpdateDate: toDateInput(item.completedUpdateDate),
      notes: item.notes ?? '',
    });
  };

  const cancelEditAssignment = () => {
    setEditingItem(null);
    setEditDraft(EMPTY_MATRIX_DRAFT);
  };

  const saveEditAssignment = async () => {
    if (!editingItem) return;

    setSavingEdit(true);
    try {
      // Same upsert endpoint the Assign popup uses, keyed on (clientId, feedItemId). Notes are
      // sent as a string - never null - so clearing the box here deliberately deletes the note,
      // while the Assign popup's untouched (null) box leaves it alone.
      await api.post('/clients/feed-item-status', {
        clientId: editingItem.client.id,
        feedItemId: editingItem.feedItem.id,
        status: editDraft.status,
        vulnerabilityLevel: editDraft.vulnerabilityLevel || null,
        plannedUpdateDate: editDraft.plannedUpdateDate || null,
        completedUpdateDate: editDraft.completedUpdateDate || null,
        notes: editDraft.notes,
      });

      toast.success('Assignment updated');
      cancelEditAssignment();
      await loadMatrixData();
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update assignment'));
      console.error('Error updating assignment:', error);
    } finally {
      setSavingEdit(false);
    }
  };

  const loadMatrixData = async () => {
    try {
      setLoading(true);
      const response = await api.get('/clients/matrix');
      setMatrixData(response.data);
    } catch (error) {
      toast.error('Failed to load matrix data');
      console.error('Error loading matrix:', error);
    } finally {
      setLoading(false);
    }
  };

  const uniqueClients = Array.from(
    new Set(matrixData.map((item) => item.client.name))
  ).sort();

  const filteredData = matrixData.filter((item) => {
    if (filterClient && item.client.name !== filterClient) return false;
    if (filterStatus && item.status !== filterStatus) return false;
    if (filterVulnerability && item.vulnerabilityLevel !== filterVulnerability)
      return false;
    return true;
  });

  // Group by client
  const groupedByClient = filteredData.reduce((acc, item) => {
    if (!acc[item.client.name]) {
      acc[item.client.name] = [];
    }
    acc[item.client.name].push(item);
    return acc;
  }, {} as Record<string, MatrixItem[]>);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner className="h-8 w-8 text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Client Update Matrix</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Track vulnerability and update status for each client
        </p>
      </div>

      {/* Legend */}
      <div className="card p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Status
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(STATUS_META).map((status) => (
                <StatusBadge key={status} status={status} />
              ))}
            </div>
          </div>
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Vulnerability
            </p>
            <div className="flex flex-wrap gap-2">
              {Object.keys(VULNERABILITY_META).map((level) => (
                <VulnerabilityBadge key={level} level={level} />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <label htmlFor="filter-client" className="label mb-1 block">
              Client
            </label>
            <select
              id="filter-client"
              value={filterClient}
              onChange={(e) => setFilterClient(e.target.value)}
              className="input"
            >
              <option value="">All Clients</option>
              {uniqueClients.map((client) => (
                <option key={client} value={client}>
                  {client}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="filter-status" className="label mb-1 block">
              Status
            </label>
            <select
              id="filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="input"
            >
              <option value="">All Statuses</option>
              {Object.keys(STATUS_META).map((status) => (
                <option key={status} value={status}>
                  {STATUS_META[status].label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="filter-vulnerability" className="label mb-1 block">
              Vulnerability Level
            </label>
            <select
              id="filter-vulnerability"
              value={filterVulnerability}
              onChange={(e) => setFilterVulnerability(e.target.value)}
              className="input"
            >
              <option value="">All Levels</option>
              {Object.keys(VULNERABILITY_META).map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Matrix */}
      {Object.keys(groupedByClient).length === 0 ? (
        <div className="card">
          <EmptyState
            icon={LayoutGrid}
            title="No data available"
            description="Start tracking updates by assigning feed items to clients."
          />
        </div>
      ) : (
        <div className="space-y-6 overflow-x-auto">
          {Object.entries(groupedByClient).map(([clientName, items]) => (
            <div key={clientName} className="card overflow-hidden">
              {/* Client Header (sticky as the section scrolls) */}
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-surface px-6 py-4">
                <div>
                  <h2 className="text-lg font-semibold text-foreground">{clientName}</h2>
                  <p className="text-sm text-muted-foreground">
                    {items.length} update{items.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>

              {/* Items */}
              <div className="divide-y divide-border">
                {items.map((item) => (
                  <div key={item.id} className="p-6 transition-colors hover:bg-muted/60">
                    <div className="mb-3 flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <h3 className="mb-1 text-base font-medium text-foreground">
                          {item.feedItem.title}
                        </h3>
                        <p className="line-clamp-2 text-sm text-muted-foreground">
                          {item.feedItem.description}
                        </p>
                      </div>

                      {item.vulnerabilityLevel && (
                        <VulnerabilityBadge level={item.vulnerabilityLevel} />
                      )}
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <StatusBadge status={item.status} />

                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
                        {new Date(item.feedItem.publishedDate).toLocaleDateString()}
                      </span>

                      {item.plannedUpdateDate && (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                          Planned: {new Date(item.plannedUpdateDate).toLocaleDateString()}
                        </span>
                      )}

                      {item.completedUpdateDate && (
                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                          <CalendarCheck2 className="h-3.5 w-3.5" aria-hidden="true" />
                          Completed: {new Date(item.completedUpdateDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>

                    {item.notes && (
                      <div className="mt-3 rounded-md border border-border bg-muted/40 p-3">
                        <p className="text-sm text-foreground">{item.notes}</p>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-4">
                      <button
                        type="button"
                        onClick={() => startEditAssignment(item)}
                        className="btn-secondary btn-sm gap-1.5"
                        aria-label={`Edit assignment of ${item.feedItem.title} to ${item.client.name}`}
                      >
                        <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                        Edit assignment
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedMatrixItem(item);
                          setIsCreateTaskModalOpen(true);
                        }}
                        className="btn-primary btn-sm"
                        aria-label={`Create task for ${item.feedItem.title} (${clientName})`}
                      >
                        <Plus className="h-4 w-4" aria-hidden="true" />
                        Create Task
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Task Modal */}
      {/* Edit assignment (SC-256) */}
      {editingItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-secondary-950/50 p-4 backdrop-blur-xs">
          <div
            className="surface-overlay w-full max-w-md animate-slide-up p-6 shadow-modal max-h-[90vh] overflow-y-auto"
            role="dialog"
            aria-modal="true"
            aria-label="Edit assignment"
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-foreground">Edit assignment</h2>
                <p className="mt-0.5 truncate text-sm text-muted-foreground">
                  {editingItem.client.name} — {editingItem.feedItem.title}
                </p>
              </div>
              <button
                type="button"
                onClick={cancelEditAssignment}
                className="btn-icon btn-ghost btn-sm shrink-0"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="label" htmlFor="edit-assignment-status">Status</label>
                <select
                  id="edit-assignment-status"
                  value={editDraft.status}
                  onChange={(e) => setEditDraft({ ...editDraft, status: e.target.value })}
                  className="input mt-1.5"
                >
                  {Object.keys(STATUS_META).map((key) => (
                    <option key={key} value={key}>{STATUS_META[key].label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="label" htmlFor="edit-assignment-vulnerability">Vulnerability</label>
                <select
                  id="edit-assignment-vulnerability"
                  value={editDraft.vulnerabilityLevel}
                  onChange={(e) => setEditDraft({ ...editDraft, vulnerabilityLevel: e.target.value })}
                  className="input mt-1.5"
                >
                  <option value="">Not set</option>
                  {Object.keys(VULNERABILITY_META).map((key) => (
                    <option key={key} value={key}>{VULNERABILITY_META[key].label}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label" htmlFor="edit-assignment-planned">Planned</label>
                  <input
                    id="edit-assignment-planned"
                    type="date"
                    value={editDraft.plannedUpdateDate}
                    onChange={(e) => setEditDraft({ ...editDraft, plannedUpdateDate: e.target.value })}
                    className="input mt-1.5"
                  />
                </div>
                <div>
                  <label className="label" htmlFor="edit-assignment-completed">Completed</label>
                  <input
                    id="edit-assignment-completed"
                    type="date"
                    value={editDraft.completedUpdateDate}
                    onChange={(e) => setEditDraft({ ...editDraft, completedUpdateDate: e.target.value })}
                    className="input mt-1.5"
                  />
                </div>
              </div>

              <div>
                <label className="label" htmlFor="edit-assignment-notes">Notes</label>
                <textarea
                  id="edit-assignment-notes"
                  value={editDraft.notes}
                  onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                  rows={4}
                  className="input mt-1.5"
                  placeholder="Context for this client's update"
                />
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button
                type="button"
                onClick={cancelEditAssignment}
                disabled={savingEdit}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveEditAssignment}
                disabled={savingEdit}
                className="btn-primary flex-1 gap-2"
              >
                {savingEdit && <Spinner className="text-primary-foreground" />}
                {savingEdit ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      <CreateTaskModal
        isOpen={isCreateTaskModalOpen}
        onClose={() => {
          setIsCreateTaskModalOpen(false);
          setSelectedMatrixItem(null);
        }}
        onTaskCreated={() => {
          loadMatrixData();
          setIsCreateTaskModalOpen(false);
          setSelectedMatrixItem(null);
        }}
        feedItemId={selectedMatrixItem?.feedItem.id}
        feedItemTitle={selectedMatrixItem?.feedItem.title}
        feedItemDescription={selectedMatrixItem?.feedItem.description}
        clientId={selectedMatrixItem?.client.id}
        clientName={selectedMatrixItem?.client.name}
      />
    </div>
  );
}
